// @ts-nocheck
import React from 'react'
import { filter, join, upperFirst } from 'lodash'
import { colors } from '@lmc/common/constants'
import {
  convertPluralQuantityToSingular,
  convertDoseNumberToWord,
} from '../utils'
import { getFrequencyDescription } from '../../../fields/types/MedicationFrequencyField/utils'

interface EmarMedicationAllDetailsProps {
  displayTitle?: boolean
  fields: Record<string, any>
  formState: Record<string, any>
}

const renderMedicationDetailsTitle = (props: EmarMedicationAllDetailsProps) => {
  const { displayTitle = true } = props

  if (!displayTitle) return null

  return (
    <div style={styles.detailsTitleContainer}>
      <h3 style={styles.detailsTitle}>Medication Details</h3>
      <div style={styles.detailsTitleUnderline} />
    </div>
  )
}

const renderDetail = (detail: any, comment: any) => {
  return (
    <div>
      <div>{detail}</div>
      <div>{comment}</div>
    </div>
  )
}

const renderQuantityDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { quantity, quantityType, quantity_comment },
  } = props
  const detail = `${quantity} ${quantityType}`

  return renderDetail(detail, quantity_comment)
}

const getFixedDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    // @ts-ignore
    altDose,
    // @ts-ignore
    dose,
    fields: { quantityType, variableDose },
  } = props

  const doseWord = convertDoseNumberToWord(dose, quantityType)
  const altDoseWord = convertDoseNumberToWord(altDose, quantityType)
  const quantityTypeWord = quantityType
    ? !variableDose && dose === 1
      ? convertPluralQuantityToSingular(quantityType)
      : quantityType
    : ''

  return `${doseWord}${altDose ? ` or ${altDoseWord}` : ''} ${quantityTypeWord}`
}

const getNonFixedDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const { checkedEmarRoundsDetails } = props

  return (
    <div>
      {checkedEmarRoundsDetails.map((round) => {
        const doseText = getFixedDoseDetail({
          ...props,
          dose: round.dose,
          altDose: round.altDose,
        })
        return (
          <div key={round.emarRoundTiming}>
            {`${doseText} at ${round.time}`}
          </div>
        )
      })}
    </div>
  )
}

const renderDoseDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { altDose, dose, emarRoundsDetails, PRN },
  } = props

  if (PRN) {
    if (dose) {
      return getFixedDoseDetail({ ...props, dose, altDose })
    } 
      return getFixedDoseDetail({
        ...props,
        dose: emarRoundsDetails[0].dose,
        altDose: emarRoundsDetails[0].altDose,
      })
    
  }

  const checkedEmarRoundsDetails =
    emarRoundsDetails[0].isChecked == null
      ? emarRoundsDetails
      : filter(emarRoundsDetails, 'isChecked')

  const doseText = getNonFixedDoseDetail({ ...props, checkedEmarRoundsDetails })

  return doseText
}

const renderHoursDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { emarRoundsDetails, PRN },
  } = props

  if (PRN) return 'PRN'

  const checkedEmarRoundsDetails =
    emarRoundsDetails[0].isChecked == null
      ? emarRoundsDetails
      : filter(emarRoundsDetails, 'isChecked')

  const times = checkedEmarRoundsDetails.map((round) => round.time)

  return join(times, ', ')
}

const renderEndDateDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields: { endDate, hasEndDate_comment },
  } = props
  const detail = endDate || 'Ongoing'

  return renderDetail(detail, hasEndDate_comment)
}

const renderFrequencyDetail = (props: EmarMedicationAllDetailsProps) => {
  const {
    fields,
    fields: { freqInterval },
  } = props

  if (!freqInterval) return null

  return upperFirst(
    getFrequencyDescription({ ...fields, freqInterval: `${freqInterval}` })
  )
}

const renderSpecialDetail = (props: EmarMedicationAllDetailsProps) => {
  const { key } = props

  switch (key) {
    case 'quantity':
      return renderQuantityDetail(props)
    case 'dose':
      return renderDoseDetail(props)
    case 'hours':
      return renderHoursDetail(props)
    case 'endDate':
      return renderEndDateDetail(props)
    case 'freqInterval':
      return renderFrequencyDetail(props)
    default:
      return null
  }
}

const renderDetailRowInfo = (props: EmarMedicationAllDetailsProps) => {
  const { commentKey, fields, key } = props

  const specialKeys = ['quantity', 'dose', 'hours', 'endDate', 'freqInterval']

  if (specialKeys.includes(key)) {
    return renderSpecialDetail(props)
  } if (typeof fields[key] === 'boolean') {
    const detail = fields[key] ? 'Yes' : 'No'
    const comment = commentKey && fields[commentKey]

    return renderDetail(detail, comment)
  }

  return fields[key]
}

const renderMedicationDetailsList = (props: EmarMedicationAllDetailsProps) => {
  const { fields } = props

  const medDetailsOrder = [
    { key: 'PRN', title: 'PRN' },
    { key: 'dose', title: 'Dose' },
    { key: 'hours', title: 'Hours' },
    { key: 'startDate', title: 'Start Date' },
    { key: 'freqInterval', title: 'Frequency' },
    { key: 'endDate', title: 'End Date' },
    { key: 'cautionaryInstructions', title: 'Cautionary Instructions' },
    { key: 'route', title: 'Route' },
    {
      key: 'secondSignatureRequired',
      title: 'Requires second signature',
      commentKey: 'secondSignatureRequired_comment',
    },
    {
      key: 'homeRemedy',
      title: 'Homely Remedy',
      commentKey: 'homeRemedy_comment',
    },
    {
      key: 'overTheCounter',
      title: 'Over the Counter',
      commentKey: 'overTheCounter_comment',
    },
    {
      key: 'blisterPackName',
      title: 'Part of a pharmacy filled compliance aid',
    },
    {
      key: 'controlledDrug',
      title: 'Controlled Drug',
      commentKey: 'controlledDrug_comment',
    },
    { key: 'description', title: 'Description' },
    { key: 'additionalSupport', title: 'Additional Support' },
    { key: 'quantity', title: 'Stock Quantity' },
  ]

  return (
    <div style={styles.detailsListContainer}>
      {medDetailsOrder.map((entry) => {
        if (
          (fields[entry.key] == null || fields[entry.key] === '') &&
          entry.key !== 'dose' &&
          entry.key !== 'hours' &&
          entry.key !== 'endDate'
        )
          return null

        return (
          <div style={styles.detailContainer} key={entry.key}>
            <div style={styles.detailTitle}>{entry.title}</div>
            <div style={styles.detailInfo}>
              {renderDetailRowInfo({
                ...props,
                key: entry.key,
                commentKey: entry.commentKey,
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const EmarMedicationAllDetails: React.FC<EmarMedicationAllDetailsProps> = (
  props
) => {
  return (
    <div>
      {renderMedicationDetailsTitle(props)}
      {renderMedicationDetailsList(props)}
    </div>
  )
}

const styles = {
  detailContainer: {
    borderBottom: `1px solid ${colors.grey}`,
    padding: '7px 0px',
  },
  detailInfo: {
    lineHeight: '22px',
  },
  detailTitle: {
    fontWeight: 700,
    lineHeight: '22px',
  },
  detailsContainer: {
    marginBottom: 32,
    width: '100%',
  },
  detailsListContainer: {
    background: colors.white,
    borderRadius: 15,
    padding: '5px 10px 12px',
  },
  detailsTitle: {
    fontWeight: 400,
    fontSize: 20,
    lineHeight: '30px',
    marginBottom: 3,
  },
  detailsTitleContainer: {
    marginBottom: 16,
  },
  detailsTitleUnderline: {
    border: `1px solid ${colors.grey}`,
  },
}
export default EmarMedicationAllDetails
